import React, { useState } from "react";
import { api, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import { Button } from "@finance/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { UploadSimple, DownloadSimple } from "@phosphor-icons/react";

const isNumCol = (h) => /amount|vat|charge|total|qty|quantity|price|balance|debit|credit|rate|value/i.test(h || "");
const isDateCol = (h) => /(^|\s|_)date(\s|_|$)/i.test(h || "") && !/stamp|time|update/i.test(h || "");
const fmtCell = (h, v) => {
  if (v == null || v === "") return "—";
  if (isDateCol(h)) return String(v).slice(0, 10);
  return String(v);
};

// Reusable bulk-upload: download template → upload CSV → preview + validate → commit.
export function BulkUploadButton({ entity, label = "Bulk Upload", onDone, basePath = "/bulk", title }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get(`${basePath}/${entity}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${entity}_template.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e) || "Could not download the template. Refresh and try again."); }
  };

  const doPreview = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`${basePath}/${entity}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); setPreview(null); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`${basePath}/${entity}/commit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} record(s)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" data-testid={`bulk-upload-${entity}-btn`} onClick={() => { reset(); setOpen(true); }}>
        <UploadSimple size={16} className="mr-1" />{label}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-3xl" data-testid={`bulk-upload-${entity}-dialog`}>
          <DialogHeader><DialogTitle className="font-display">{title || `Bulk Upload — ${entity.replace(/_/g, " ")}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 rounded-sm border border-dashed border-gray-300 bg-gray-50 p-3 text-sm">
              <span className="text-gray-600">1. Download the template, fill it offline, then upload it back.</span>
              <Button size="sm" variant="outline" data-testid={`bulk-template-${entity}`} onClick={downloadTemplate}><DownloadSimple size={15} className="mr-1" />Template (CSV)</Button>
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm bg-[#122E5C] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#122E5C]" data-testid={`bulk-file-${entity}`}>
                {busy && !preview ? "Reading…" : "2. Upload CSV"}
                <input type="file" className="hidden" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => { doPreview(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>

            {preview && (
              <div>
                <div className="mb-2 text-sm">
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700" data-testid="bulk-valid-count">{preview.valid_count} valid</span>
                  {preview.error_count > 0 && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-red-700" data-testid="bulk-error-count">{preview.error_count} with errors</span>}
                </div>
                <div className="max-h-72 overflow-auto rounded-sm border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-gray-50 lx-label text-gray-500"><tr>
                      <th className="whitespace-nowrap px-2 py-1.5 text-left">#</th>
                      {preview.headers.map((h) => <th key={h} className={`whitespace-nowrap px-2 py-1.5 ${isNumCol(h) ? "text-right" : "text-left"}`}>{h}</th>)}
                      <th className="sticky right-0 z-20 whitespace-nowrap bg-gray-50 px-2 py-1.5 text-left shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">Status</th>
                    </tr></thead>
                    <tbody>
                      {preview.rows.map((r) => {
                        const ok = !r.errors.length;
                        return (
                          <tr key={r.row} className={`transition-colors ${ok ? "even:bg-gray-50/50 hover:bg-gray-100/70" : "bg-red-50/60"}`}>
                            <td className="px-2 py-1.5 text-gray-400">{r.row}</td>
                            {preview.headers.map((h) => (
                              <td key={h} title={r.data[h] ? String(r.data[h]) : ""} className={`max-w-[160px] truncate px-2 py-1.5 ${isNumCol(h) ? "text-right lx-num tabular-nums" : ""}`}>{fmtCell(h, r.data[h])}</td>
                            ))}
                            <td className={`sticky right-0 z-10 whitespace-nowrap px-2 py-1.5 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)] ${ok ? "bg-white" : "bg-red-50"}`}>{ok ? <span className="font-semibold text-emerald-600">OK</span> : <span className="text-red-600">{r.errors.join("; ")}</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" disabled={busy || !preview || preview.valid_count === 0} data-testid={`bulk-commit-${entity}`} onClick={commit}>
              {busy ? "Importing…" : `Import ${preview?.valid_count || 0} row(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
